import Image from "next/image";
import type { WorldCupGroup } from "@/lib/world-cup-data";
import { initialsFromName } from "@/lib/utils";
import { getHungarianWorldCupTeamName } from "@/lib/world-cup-team-names";

function TeamMark({ team }: { team: WorldCupGroup["teams"][number] }) {
  const teamName = getHungarianWorldCupTeamName(team.name) || team.name;

  if (team.logoUrl) {
    return (
      <span className="relative h-5 w-7 shrink-0 overflow-hidden rounded-[3px] bg-white ring-1 ring-black/5">
        <Image src={team.logoUrl} alt="" fill sizes="28px" className="object-contain p-0.5" unoptimized />
      </span>
    );
  }

  if (team.flag) {
    return (
      <span className="grid h-5 w-7 shrink-0 place-items-center rounded-[3px] bg-[#FAF8F2] text-base leading-none ring-1 ring-black/5">
        {team.flag}
      </span>
    );
  }

  return (
    <span className="grid h-5 w-7 shrink-0 place-items-center rounded-[3px] bg-[#FAF8F2] text-[9px] font-bold text-[#9B6A23] ring-1 ring-black/5">
      {initialsFromName(teamName).slice(0, 2)}
    </span>
  );
}

export function WorldCupGroupCard({ group }: { group: WorldCupGroup }) {
  return (
    <article className="flex h-full flex-col overflow-hidden rounded-lg border border-[#E7DFD2] bg-[#FFFDF8] shadow-[0_8px_22px_rgba(39,62,71,0.045)] transition-[border-color,box-shadow] duration-200 hover:border-[#D8B36A] hover:shadow-[0_10px_26px_rgba(39,62,71,0.065)]">
      <div className="mb-1.5 flex items-baseline justify-between gap-3 border-b border-[#EEE6D8] pb-2">
        <h3 className="flex items-baseline gap-1.5 px-3 pt-2.5 leading-none sm:px-3.5">
          <span className="font-display text-xl font-bold text-[#2A2620]">{group.id}</span>
          <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8D8273]">csoport</span>
        </h3>
        <span className="px-3 pt-2.5 text-[9px] font-semibold uppercase tracking-[0.18em] text-[#A17A36] sm:px-3.5">
          VB 2026
        </span>
      </div>
      <div className="grid grid-cols-[2rem_minmax(6.5rem,1fr)_1.75rem_1.75rem_1.75rem_1.75rem_2rem_2rem] items-center border-b border-[#EFE7DB] px-2.5 pb-1 text-[9px] font-black uppercase tracking-[0.08em] text-[#8D8273] sm:px-3">
        <span title="Helyezés">H</span>
        <span>Csapat</span>
        <span className="text-center" title="Meccs">M</span>
        <span className="text-center" title="Győzelem">GY</span>
        <span className="text-center" title="Döntetlen">D</span>
        <span className="text-center" title="Vereség">V</span>
        <span className="text-center" title="Gólkülönbség">GK</span>
        <span className="text-center" title="Pont">P</span>
      </div>
      <div className="divide-y divide-[#EFE7DB]">
        {group.teams.map((team) => {
          const teamName = getHungarianWorldCupTeamName(team.name) || team.name;

          return (
            <div
              key={team.name}
              className="grid grid-cols-[2rem_minmax(6.5rem,1fr)_1.75rem_1.75rem_1.75rem_1.75rem_2rem_2rem] items-center px-2.5 py-2 text-[12px] font-semibold text-[#191715] sm:px-3"
            >
              <span className="tabular-nums text-[#6F624F]">{team.position}</span>
              <div className="flex min-w-0 items-center gap-1.5">
                <TeamMark team={team} />
                <span className="min-w-0 truncate leading-5">{teamName}</span>
              </div>
              <span className="text-center tabular-nums text-[#6F624F]">{team.played}</span>
              <span className="text-center tabular-nums text-[#6F624F]">{team.wins}</span>
              <span className="text-center tabular-nums text-[#6F624F]">{team.draws}</span>
              <span className="text-center tabular-nums text-[#6F624F]">{team.losses}</span>
              <span className="text-center tabular-nums text-[#6F624F]">
                {team.goalDifference > 0 ? `+${team.goalDifference}` : team.goalDifference}
              </span>
              <span className="text-center font-black tabular-nums text-[#2A2620]">{team.points}</span>
            </div>
          );
        })}
      </div>
    </article>
  );
}
